/**
 * useListingActions Hook
 *
 * Provides admin actions for managing listings via direct Firestore writes.
 *
 * These actions do NOT write to `auditLogs`: that collection is server-only by
 * design (firestore.rules `allow create: if false`) so entries can't be forged
 * by a client. The client audit write that used to follow each action threw
 * permission-denied every single time — after the listing had already been
 * updated — which made a successful approve/pause/feature/delete report failure
 * ("Couldn't approve this listing. Please try again."). Audit entries for
 * listing moderation belong in a Cloud Function (see admin/suspendUser.ts).
 */

import { useState, useCallback } from 'react';
import {
  doc,
  updateDoc,
  deleteDoc,
  writeBatch,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';
import { useAuth } from '@/contexts';

export const useListingActions = () => {
  const [isPausing, setIsPausing] = useState(false);
  const [isUnpausing, setIsUnpausing] = useState(false);
  const [isFeaturing, setIsFeaturing] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { user } = useAuth();
  const adminId = user?.uid || '';

  const pauseListing = useCallback(async (listingId: string): Promise<boolean> => {
    setIsPausing(true);
    try {
      await updateDoc(doc(db, 'listings', listingId), {
        status: 'archived',
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Failed to pause listing:', error);
      return false;
    } finally {
      setIsPausing(false);
    }
  }, []);

  const unpauseListing = useCallback(async (listingId: string): Promise<boolean> => {
    setIsUnpausing(true);
    try {
      await updateDoc(doc(db, 'listings', listingId), {
        status: 'published',
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Failed to unpause listing:', error);
      return false;
    } finally {
      setIsUnpausing(false);
    }
  }, []);

  const featureListing = useCallback(
    async (listingId: string, featured: boolean): Promise<boolean> => {
      setIsFeaturing(true);
      try {
        await updateDoc(doc(db, 'listings', listingId), {
          isFeatured: featured,
          updatedAt: serverTimestamp(),
        });
        return true;
      } catch (error) {
        console.error('Failed to feature listing:', error);
        return false;
      } finally {
        setIsFeaturing(false);
      }
    },
    []
  );

  const approveListing = useCallback(async (listingId: string): Promise<boolean> => {
    setIsApproving(true);
    try {
      await updateDoc(doc(db, 'listings', listingId), {
        status: 'published',
        approvedAt: serverTimestamp(),
        approvedBy: adminId,
        updatedAt: serverTimestamp(),
      });
      return true;
    } catch (error) {
      console.error('Failed to approve listing:', error);
      return false;
    } finally {
      setIsApproving(false);
    }
  }, [adminId]);

  const rejectListing = useCallback(
    async (listingId: string, reason: string): Promise<boolean> => {
      setIsApproving(true);
      try {
        await updateDoc(doc(db, 'listings', listingId), {
          status: 'draft',
          rejectionReason: reason,
          rejectedAt: serverTimestamp(),
          rejectedBy: adminId,
          updatedAt: serverTimestamp(),
        });
        return true;
      } catch (error) {
        console.error('Failed to reject listing:', error);
        return false;
      } finally {
        setIsApproving(false);
      }
    },
    [adminId]
  );

  const deleteListing = useCallback(async (listingId: string): Promise<boolean> => {
    setIsDeleting(true);
    try {
      await deleteDoc(doc(db, 'listings', listingId));
      return true;
    } catch (error) {
      console.error('Failed to delete listing:', error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const bulkPause = useCallback(async (listingIds: string[]): Promise<boolean> => {
    setIsPausing(true);
    try {
      const batch = writeBatch(db);
      listingIds.forEach((id) => {
        batch.update(doc(db, 'listings', id), { status: 'archived', updatedAt: serverTimestamp() });
      });
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Failed to bulk pause:', error);
      return false;
    } finally {
      setIsPausing(false);
    }
  }, []);

  const bulkUnpause = useCallback(async (listingIds: string[]): Promise<boolean> => {
    setIsUnpausing(true);
    try {
      const batch = writeBatch(db);
      listingIds.forEach((id) => {
        batch.update(doc(db, 'listings', id), { status: 'published', updatedAt: serverTimestamp() });
      });
      await batch.commit();
      return true;
    } catch (error) {
      console.error('Failed to bulk unpause:', error);
      return false;
    } finally {
      setIsUnpausing(false);
    }
  }, []);

  const bulkFeature = useCallback(
    async (listingIds: string[], featured: boolean): Promise<boolean> => {
      setIsFeaturing(true);
      try {
        const batch = writeBatch(db);
        listingIds.forEach((id) => {
          batch.update(doc(db, 'listings', id), { isFeatured: featured, updatedAt: serverTimestamp() });
        });
        await batch.commit();
        return true;
      } catch (error) {
        console.error('Failed to bulk feature:', error);
        return false;
      } finally {
        setIsFeaturing(false);
      }
    },
    []
  );

  return {
    pauseListing,
    unpauseListing,
    featureListing,
    approveListing,
    rejectListing,
    deleteListing,
    bulkPause,
    bulkUnpause,
    bulkFeature,
    isPausing,
    isUnpausing,
    isFeaturing,
    isApproving,
    isDeleting,
  };
};

export default useListingActions;
